import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SalesRep } from 'src/app/core/models/sales-rep.model';
import { User } from 'src/app/core/models/user.model';
import { SalesRepService } from 'src/app/core/services/sales-rep.service';
import { UserService } from 'src/app/core/services/user.service';
import { ChangeDetectorRef } from '@angular/core';
import { InvoiceService } from 'src/app/core/services/invoice.service';
import { Invoice } from 'src/app/core/models/invoice.model';
import { BoardService } from 'src/app/core/services/board.service';
import { BoardStatService } from 'src/app/core/services/boardstats.service';
import { LocationService } from 'src/app/core/services/location.service';

@Component({
  selector: 'app-salesrep',
  templateUrl: './salesrep.component.html',
  styleUrls: ['./salesrep.component.scss']
})
export class SalesRepComponent implements OnInit {

  term: any;
  chatData: any[];
  salesReps: SalesRep[];
  statData: any[];
  user: User[];
  invoice: Invoice[];
  filteredSalesRep: SalesRep[];
  sortColumn: 'name' | 'customerNumber' | 'contact' | 'monthlyPayment' | 'numBoards' | 'enabled' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  collectors: User[] = [];
  collectorId: [null]  // kept as-is
  top10ByBoards: SalesRep[] = [];
  top10ByMonthly: SalesRep[] = [];

  selectedRep: SalesRep | null = null;
  repMachinesLoading = false;
  repMachines: Array<{
    assetTag: string;
    serialNumber: string;
    gamePackageName: string;
    locationName: string;
    city: string;
    state: string;
    cashIn: number;
    cashOut: number;
    net: number;
    lastRead: string | Date;
  }> = [];

  trackRep = (_: number, r: SalesRep) => r.id;

  addEditMode: any;
  saleRepAddEditFormGroup: FormGroup;

  constructor(
    private salesRepService: SalesRepService,
    private userService: UserService,
    private modalService: NgbModal,
    private router: Router,
    private tastr: ToastrService,
    private invoiceService: InvoiceService,
    private boardService: BoardService,
    private boardStatsService: BoardStatService,
    private locationService: LocationService,
    private cdr: ChangeDetectorRef
  ) {}

  // bread crumb items
  breadCrumbItems: Array<{}>;

  async ngOnInit(): Promise<void> {
    this.breadCrumbItems = [{ label: 'Fortune' }, { label: 'Sales Rep', active: true }];
    this.userService.getAll().then(users => {
      this.collectors = users.filter(u => u.userType === 'INTERNAL' || u.userType === 'TECH' || u.userType === 'COLLECTOR');
    });

    await this.refresh();
  }

  async refresh(): Promise<void> {
    // 1) Fetch
    this.salesReps = await this.salesRepService.getAll();

    // Keep board counts aligned with the Locations page by deriving totals from location.numBoards.
    const locations = await this.locationService.getAll();
    const boardTotalsByRep = this.buildBoardTotalsByRep(locations || []);

    this.salesReps = (this.salesReps || []).map((rep) => {
      const normalizedRepId = this.normalizeId(rep?.id);
      if (!normalizedRepId) {
        return rep;
      }

      const derivedBoards = boardTotalsByRep.get(normalizedRepId);
      if (derivedBoards == null) {
        return rep;
      }

      return {
        ...rep,
        numBoards: derivedBoards,
      };
    });

    // 2) Recalculate amounts (updates monthlyPayment on each)
    this.calculateAllAmounts(this.salesReps);

    // 3) Build filtered + sorted view (most boards first, warehouses last)
    this.updateFilteredSalesRep();

    // 4) Build toplists (after monthlyPayment is computed)
    this.buildToplists();

    // Optional sanity log
    console.log('SalesReps after calculation:', this.salesReps);
  }

  private normalizeId(value?: string): string {
    return (value || '').replace(/-/g, '').toLowerCase();
  }

  private buildBoardTotalsByRep(locations: any[]): Map<string, number> {
    const totals = new Map<string, number>();

    for (const location of locations) {
      const repId = this.normalizeId(location?.salesRep?.id);
      if (!repId) {
        continue;
      }

      const locationBoards = Number(location?.numBoards ?? 0);
      const safeBoards = Number.isFinite(locationBoards) && locationBoards > 0 ? locationBoards : 0;
      totals.set(repId, (totals.get(repId) || 0) + safeBoards);
    }

    return totals;
  }

  onManageUsers(rep: SalesRep) {
    this.router.navigate(['/salesreps/users'], { queryParams: { salesRepId: rep.id }});
  }

  async onChange() {
    console.log("onChange() called", this.saleRepAddEditFormGroup.value);
    this.refresh();
  }

  async onCheckBoxChanged(event: any, salesRep: SalesRep) {
    const newEnabledState = event?.target?.checked;
    if (salesRep.enabled != newEnabledState) {
      await this.salesRepService.setEnabled(salesRep.id, newEnabledState);
    }
  }

  async onTenDollarCheckBoxChanged(event: any, salesRep: SalesRep) {
    const newEnabledState = event.target.checked;
    console.log(`Updating tenDollarEnabled  to: ${newEnabledState}`);
    try {
      await this.salesRepService.setTenDollarEnabled(salesRep.id, newEnabledState);
      salesRep.tenDollarEnabled = newEnabledState;
      console.log('Update successful');
    } catch (error) {
      console.error('Failed to update:', error);
    }
  }

  async onFiveDollarCheckBoxChanged(event: any, salesRep: SalesRep) {
    const newFiveDollarEnabledState = event?.target?.checked;
    if (salesRep.fiveDollarEnabled != newFiveDollarEnabledState) {
      await this.salesRepService.setFiveDollarEnabled(salesRep.id, newFiveDollarEnabledState);
      this.cdr.detectChanges();
    }
  }

  async onTextCheckBoxChanged(event: any, salesRep: SalesRep) {
    const newEnabledState = event?.target?.checked;
    if (salesRep.autoSendActivationText != newEnabledState) {
      await this.salesRepService.setTextEnabled(salesRep.id, newEnabledState);
    }
  }

  calculateTotal(salesRep: SalesRep): number {
    let amount = 0;
    if (salesRep.tenDollarEnabled && salesRep.numBoards > 0) {
      amount = 10 * salesRep.numBoards * 30;
    } else if (salesRep.fiveDollarEnabled && salesRep.numBoards > 0) {
      amount = 5 * salesRep.numBoards * 30;
    }
    salesRep.monthlyPayment = amount;
    return amount;
  }

  // Recalculate monthly for all
  calculateAllAmounts(salesReps: SalesRep[]): void {
    salesReps.forEach(salesRep => this.calculateTotal(salesRep));
  }

  // salesrep.component.ts
  async updateSalesRepInvoiceStatus(salesRep: SalesRep, event: any): Promise<void> {
    const newStatus = event.target.checked ? 'paid' : 'pending';
    if (salesRep) {
      try {
        const updatedSalesrep = await this.salesRepService.updateLastInvoiceStatus(salesRep, newStatus);
        console.log('Invoice status updated successfully:', updatedSalesrep);
        salesRep.lastInvoice = updatedSalesrep.lastInvoice;
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error updating invoice status:', error);
      }
    } else {
      console.error('Invoice ID is missing');
    }
  }

updateFilteredSalesRep(filter: any = null) {
  if (filter) {
    const terms = String(filter).toLowerCase().trim().split(/\s+/);
    this.filteredSalesRep = this.salesReps.filter(s => {
      const searchableText = [
        s.firstName,
        s.lastName,
        `${s.firstName || ''} ${s.lastName || ''}`,
        `${s.lastName || ''} ${s.firstName || ''}`,
        s.customerNumber,
        s.primaryEmail,
      ].filter(Boolean).join(' ').toLowerCase();

      return terms.every(term => searchableText.includes(term));
    });
  } else {
    this.filteredSalesRep = [...this.salesReps];
  }

  // 🚫 Hide the special Warehouse Tech account
  this.filteredSalesRep = this.filteredSalesRep.filter(r => !this.isWarehouseTech(r));

  // ⬇️ Keep your existing “warehouse → bottom, others sorted by boards” behavior
  this.filteredSalesRep = this.sortRepsForDisplay(this.filteredSalesRep);
  this.applyTableSort();

  // keep insights in sync if you want them live
  this.buildToplists();
}


  sortBy(column: 'name' | 'customerNumber' | 'contact' | 'monthlyPayment' | 'numBoards' | 'enabled'): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyTableSort();
  }

  private applyTableSort(): void {
    if (!this.sortColumn) return;

    const column = this.sortColumn;
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    const valueFor = (rep: SalesRep): string | number => {
      switch (column) {
        case 'name':
          return `${rep.firstName || ''} ${rep.lastName || ''}`.trim();
        case 'contact':
          return rep.primaryEmail || rep.phoneNumber || '';
        case 'monthlyPayment':
          return rep.monthlyPayment || 0;
        case 'numBoards':
          return rep.numBoards || 0;
        case 'enabled':
          return rep.enabled ? 1 : 0;
        default:
          return rep.customerNumber || '';
      }
    };

    this.filteredSalesRep = [...this.filteredSalesRep].sort((a, b) => {
      const aValue = valueFor(a);
      const bValue = valueFor(b);
      const comparison = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
      return comparison * direction;
    });
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'mdi-swap-vertical';
    return this.sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
  }

  onSearch() {
    const termLower = (this.term || '').toLowerCase().trim();
    this.updateFilteredSalesRep(termLower);
  }

  async onAddNew(modal: unknown) {
    this.addEditMode = "Add";
    const currentSalesReps = await this.salesRepService.getAll();
    const nextCustomerNumber = this.generateNextCustomerNumber(currentSalesReps || []);
    this.saleRepAddEditFormGroup = new FormGroup({
      id: new FormControl<string>(''),
      username: new FormControl<string>(''),
      customerNumber: new FormControl<string>(nextCustomerNumber),
      firstName: new FormControl<string>(''),
      lastName: new FormControl<string>(''),
      phoneNumber: new FormControl<string>(''),
      primaryEmail: new FormControl<string>(''),
      enabled: new FormControl<boolean>(true),
      awsConfirmationCode: new FormControl<string>(''),
      collectorId: new FormControl<string>(''),
      businessAddress: new FormGroup({
        id: new FormControl<string>(''),
        streetAddress1: new FormControl<string>(''),
        streetAddress2: new FormControl<string>(''),
        city: new FormControl<string>(''),
        state: new FormControl<string>(''),
        zipCode: new FormControl<string>(''),
        country: new FormControl<string>(''),
      }),
    });
    try {
      const results = await this.modalService.open(modal).result;
      if (results == "Save click") {
        const data = this.saleRepAddEditFormGroup.value;
        const latestSalesReps = await this.salesRepService.getAll();
        const normalizedUsername = (data.username || '').trim().toLowerCase();
        const usernameExists = (latestSalesReps || []).some(
          rep => (rep.username || '').trim().toLowerCase() === normalizedUsername
        );

        if (usernameExists) {
          this.tastr.error(`Username "${data.username}" is already in use.`);
          return;
        }

        const existingNumbers = new Set(
          (latestSalesReps || []).map(rep => (rep.customerNumber || '').trim())
        );

        if (!data.customerNumber || existingNumbers.has(data.customerNumber.trim())) {
          data.customerNumber = this.generateNextCustomerNumber(latestSalesReps || []);
          this.saleRepAddEditFormGroup.patchValue({ customerNumber: data.customerNumber });
        }

        if (data.phoneNumber) {
          const phoneDigits = data.phoneNumber.replace(/\D/g, '');
          if (phoneDigits.length === 10) {
            data.phoneNumber = `+1${phoneDigits}`;
          } else if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) {
            data.phoneNumber = `+${phoneDigits}`;
          } else {
            data.phoneNumber = data.phoneNumber.trim();
          }
        }
        data.username = (data.username || '').trim();
        data.collectorId = data.collectorId || null;
        await this.salesRepService.create(data);
        this.tastr.success(`Sales rep created with customer number ${data.customerNumber}.`);
      }
    } catch (error) {
      this.tastr.error('Could not create the sales rep. Please try again.');
    }
    await this.refresh();
  }

  private generateNextCustomerNumber(salesReps: SalesRep[]): string {
    let highestNumber = 0;
    let width = 1;

    for (const rep of salesReps) {
      const match = (rep.customerNumber || '').trim().match(/^(.*?)(\d+)$/);
      if (!match) continue;

      const number = Number(match[2]);
      if (Number.isSafeInteger(number) && number >= highestNumber) {
        highestNumber = number;
        width = match[2].length;
      }
    }

    return `S${String(highestNumber + 1).padStart(width, '0')}`;
  }

  async onDelete(id: string) {
    await this.salesRepService.delete(id);
    await this.refresh();
  }

  async formatPhoneNumber(phoneNumber: string): Promise<string> {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return Promise.resolve(`+1 ${match[1]}-${match[2]}-${match[3]}`);
    return Promise.resolve(phoneNumber);
  }

  async onResetPassword(user: User) {
    await this.userService.resetPassword(user.id);
    this.refresh();
    this.tastr.success('Password Reset sent.');
  }

  async onEdit(original: SalesRep, modal: unknown) {
    this.addEditMode = "Edit";
    this.saleRepAddEditFormGroup = new FormGroup({
      id: new FormControl<string>(original.id),
      username: new FormControl<string>(original.username),
      customerNumber: new FormControl<string>(original.customerNumber),
      firstName: new FormControl<string>(original.firstName),
      lastName: new FormControl<string>(original.lastName),
      phoneNumber: new FormControl<string>(original.phoneNumber),
      primaryEmail: new FormControl<string>(original.primaryEmail),
      enabled: new FormControl<boolean>(original.enabled),
      awsConfirmationCode: new FormControl<string>(original.awsConfirmationCode),
      collectorId: new FormControl<string>(original.collector?.id ?? ''),
      businessAddress: new FormGroup({
        id: new FormControl<string>(original.businessAddress.id),
        streetAddress1: new FormControl<string>(original.businessAddress.streetAddress1),
        streetAddress2: new FormControl<string>(original.businessAddress.streetAddress2),
        city: new FormControl<string>(original.businessAddress.city),
        state: new FormControl<string>(original.businessAddress.state),
        zipCode: new FormControl<string>(original.businessAddress.zipCode),
        country: new FormControl<string>(original.businessAddress.country),
      }),
    });

    try {
      const results = await this.modalService.open(modal).result;
      if (results === "Save click") {
        const raw = this.saleRepAddEditFormGroup.getRawValue();
        const data = {
          ...raw,
          phoneNumber: raw.phoneNumber ? raw.phoneNumber.trim() : raw.phoneNumber,
          collectorId: raw.collectorId || null,
        };
        await this.salesRepService.update(original.id, data);
      }
    } catch (error) {
      console.error("onEdit()", { error });
    }

    await this.refresh();
  }

  // Warehouse heuristic — tweak if you have an explicit flag
  private isWarehouseRep(rep: SalesRep): boolean {
    const blob = `${rep.firstName || ''} ${rep.lastName || ''} ${rep.username || ''}`.toLowerCase();
    return blob.includes('warehouse');
  }

  // Sort non-warehouse by boards desc, then monthly desc, then name; append warehouses
  private sortRepsForDisplay(list: SalesRep[]): SalesRep[] {
    const nonWarehouse: SalesRep[] = [];
    const warehouses: SalesRep[] = [];

    for (const r of list ?? []) {
      (this.isWarehouseRep(r) ? warehouses : nonWarehouse).push(r);
    }

    nonWarehouse.sort((a, b) => {
      const aBoards = a?.numBoards ?? 0;
      const bBoards = b?.numBoards ?? 0;
      if (bBoards !== aBoards) return bBoards - aBoards;

      const aMonthly = a?.monthlyPayment ?? 0;
      const bMonthly = b?.monthlyPayment ?? 0;
      if (bMonthly !== aMonthly) return bMonthly - aMonthly;

      const aName = `${a.lastName || ''} ${a.firstName || ''}`.trim();
      const bName = `${b.lastName || ''} ${b.firstName || ''}`.trim();
      return aName.localeCompare(bName);
    });

    return [...nonWarehouse, ...warehouses];
  }
private buildToplists(): void {
  const base = (this.salesReps ?? []).filter(r => !this.isWarehouseTech(r));

  this.top10ByBoards = [...base]
    .sort((a, b) => (b?.numBoards ?? 0) - (a?.numBoards ?? 0))
    .slice(0, 10);

  this.top10ByMonthly = [...base]
    .sort((a, b) => (b?.monthlyPayment ?? 0) - (a?.monthlyPayment ?? 0))
    .slice(0, 10);
}


  async openRepMachines(rep: SalesRep, modalRef: any) {
    this.selectedRep = rep;
    this.repMachines = [];
    this.repMachinesLoading = true;
    this.modalService.open(modalRef, { size: 'xl', scrollable: true });

    try {
      // Replace with your own endpoint if different
      const boards = await this.boardService.getBoardsBySalesRepId(rep.id);

      // Month-to-date style snapshot using latest deltas
      const rows = await Promise.all(
        boards.map(async (b: any) => {
          const latest = await this.boardStatsService.getLatestByBoardId(b.id);
          const loc = b.location ?? b.currentDeployment?.location ?? {};
          return {
            assetTag: b.assetTag,
            serialNumber: b.serialNumber,
            gamePackageName: b.installedGamePackage?.name ?? b.currentDeployment?.installedGamePackage?.name ?? '',
            locationName: loc?.locationName ?? '',
            city: loc?.businessAddress?.city ?? '',
            state: loc?.businessAddress?.state ?? '',
            cashIn: latest?.cashInDiff ?? 0,
            cashOut: latest?.cashOutDiff ?? 0,
            net: latest?.profit ?? ((latest?.cashInDiff ?? 0) - (latest?.cashOutDiff ?? 0)),
            lastRead: latest?.timeStamp
          };
        })
      );

      // Show best performers first
      this.repMachines = rows.sort((a, b) => b.net - a.net);
    } finally {
      this.repMachinesLoading = false;
    }
  }

  // Treat exactly the internal "Warehouse Tech" account as hidden from UI lists.
private isWarehouseTech(rep: SalesRep): boolean {
  const blob = `${rep.firstName || ''} ${rep.lastName || ''} ${rep.username || ''}`
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  return blob.includes('warehouse tech'); // catches "Warehouse Tech"
}


  
}
